import React from 'react'
import {IconButton as ChakraIconButton, IconButtonProps as ChakraIconButtonProps} from '@chakra-ui/react'

interface IconButtonProps extends ChakraIconButtonProps {
    variant?: 'primary' | 'secondary' | 'iconButton' | 'iconButtonBgTransparent'
    icon: React.ReactElement
    isRounded?: boolean
    isDisabled?: boolean
    size?: 'iconButton' | 'iconButtonSm' | 'iconButtonMd' | 'iconButtonLg'
}

/**
 * @name IconButton
 *
 * @component
 *
 * @description
 * Renders a button with an icon using Chakra UI's IconButton.
 *
 * @param {IconButtonProps} props - The properties object.
 * @param {string} props.variant - The variant of the button.
 * @param {React.ReactElement} props.icon - The icon to be displayed inside the button.
 * @param {boolean} [props.isRounded=false] - If true, the button will have rounded corners.
 * @param {boolean} [props.isDisabled=false] - If true, the button will be disabled.
 *
 * @returns {JSX.Element} The rendered IconButton component.
 */
const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
    ({variant = 'iconButton', icon, isRounded = false, isDisabled = false, size = 'iconButton', ...props}, ref): JSX.Element => {
        return (
            <ChakraIconButton
                ref={ref}
                variant={variant}
                size={size}
                icon={icon}
                borderRadius={isRounded ? 'borderRadius.button.rounded' : 'borderRadius.button.base'}
                border="none"
                isDisabled={isDisabled}
                {...props}
            />
        )
    },
)

IconButton.displayName = 'IconButton'

export {IconButton}
